/**
 * Themed Select — a custom listbox that matches the app's dark/light
 * surface tokens. Replaces native <select> so the dropdown panel inherits
 * the same palette as the rest of the UI (native <select> popups are OS-
 * painted and always look off in dark mode).
 *
 * Accessible: role=combobox trigger + role=listbox panel + role=option
 * items. Arrow/Enter/Space to open, arrows to move the highlight,
 * Enter/Space to commit, Escape to close. Click outside closes.
 *
 * Consumers pass controlled value + onChange (not a change event) so the
 * API is type-safe for both string and number values.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';

export interface SelectOption<T extends string | number = string | number> {
  value: T;
  label: string;
  disabled?: boolean;
}

export interface SelectProps<T extends string | number = string | number> {
  value: T | null | undefined;
  onChange: (value: T) => void;
  options: SelectOption<T>[];
  placeholder?: string;
  label?: string;
  helperText?: string;
  error?: boolean | string;
  disabled?: boolean;
  className?: string;
  id?: string;
  'aria-label'?: string;
}

export function Select<T extends string | number = string>({
  value,
  onChange,
  options,
  placeholder,
  label,
  helperText,
  error,
  disabled = false,
  className = '',
  id,
  'aria-label': ariaLabel,
}: SelectProps<T>) {
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState<number>(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const autoId = useMemo(() => id || `sel-${Math.random().toString(36).slice(2, 8)}`, [id]);
  const selected = options.find((o) => o.value === value);
  const hasError = Boolean(error);
  const errorMessage = typeof error === 'string' ? error : undefined;

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // When opening, start the highlight on the current selection (or the
  // first enabled option). When closing, drop the highlight.
  useEffect(() => {
    if (!open) {
      setActiveIdx(-1);
      return;
    }
    const currentIdx = options.findIndex((o) => o.value === value);
    setActiveIdx(currentIdx >= 0 ? currentIdx : options.findIndex((o) => !o.disabled));
  }, [open, options, value]);

  // Keep the active option scrolled into view when the user arrows past
  // the visible bottom/top of the panel.
  useEffect(() => {
    if (!open || activeIdx < 0 || !panelRef.current) return;
    const el = panelRef.current.querySelector<HTMLElement>(`[data-idx="${activeIdx}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx, open]);

  const pick = (opt: SelectOption<T>) => {
    if (opt.disabled) return;
    onChange(opt.value);
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => {
        for (let n = i + 1; n < options.length; n++) {
          if (!options[n].disabled) return n;
        }
        return i;
      });
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => {
        for (let n = i - 1; n >= 0; n--) {
          if (!options[n].disabled) return n;
        }
        return i;
      });
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const picked = options[activeIdx];
      if (picked) pick(picked);
    }
  };

  const triggerClasses = `w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-sm sm:text-sm text-left transition-colors ${
    disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
  } ${
    hasError
      ? 'border-red-500/60 bg-[var(--surface-raised)] focus:ring-2 focus:ring-red-500/30'
      : open
        ? 'border-[var(--accent-primary)] bg-[var(--surface-raised)] ring-2 ring-[var(--accent-primary)]/25'
        : 'border-[var(--border-subtle)] bg-[var(--surface-raised)] hover:border-[var(--border-default)]'
  }`;

  return (
    <div className={`w-full ${className}`} ref={rootRef}>
      {label && (
        <label htmlFor={autoId} className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5">
          {label}
        </label>
      )}
      <div className="relative">
        <button
          id={autoId}
          type="button"
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={`${autoId}-listbox`}
          aria-label={ariaLabel}
          aria-invalid={hasError || undefined}
          disabled={disabled}
          onClick={() => !disabled && setOpen((o) => !o)}
          onKeyDown={onKeyDown}
          className={triggerClasses}
        >
          <span
            className={`flex-1 min-w-0 truncate ${
              selected ? 'text-[var(--text-primary)]' : 'text-[var(--text-muted)]'
            }`}
          >
            {selected?.label ?? placeholder ?? ''}
          </span>
          <ChevronDown
            className={`w-4 h-4 text-[var(--text-muted)] transition-transform flex-shrink-0 ${
              open ? 'rotate-180' : ''
            }`}
          />
        </button>

        {open && (
          <div
            ref={panelRef}
            id={`${autoId}-listbox`}
            role="listbox"
            tabIndex={-1}
            className="absolute z-30 left-0 right-0 mt-1 py-1 rounded-lg border border-[var(--border-subtle)] bg-[var(--surface-base)] shadow-xl max-h-64 overflow-auto"
          >
            {options.length === 0 ? (
              <div className="px-3 py-2 text-sm text-[var(--text-muted)]">—</div>
            ) : (
              options.map((opt, i) => {
                const isSelected = opt.value === value;
                const isActive = i === activeIdx;
                return (
                  <button
                    key={String(opt.value)}
                    data-idx={i}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    disabled={opt.disabled}
                    onMouseEnter={() => setActiveIdx(i)}
                    onClick={() => pick(opt)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${
                      opt.disabled
                        ? 'opacity-40 cursor-not-allowed text-[var(--text-muted)]'
                        : isActive
                          ? 'bg-[var(--surface-raised)] text-[var(--text-primary)]'
                          : 'text-[var(--text-primary)] hover:bg-[var(--surface-raised)]'
                    }`}
                  >
                    <span className="flex-1 min-w-0 truncate">{opt.label}</span>
                    {isSelected && (
                      <Check className="w-4 h-4 text-[var(--accent-primary)] flex-shrink-0" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>

      {errorMessage && (
        <p className="mt-1 text-sm text-red-600">{errorMessage}</p>
      )}
      {helperText && !errorMessage && (
        <p className="mt-1 text-sm text-[var(--text-muted)]">{helperText}</p>
      )}
    </div>
  );
}

export default Select;
